<script setup lang="ts">
import { ButtonComponent, DialogModal, FlexComponent, InfoIcon, TypographyComponent } from '@metapic-apps/shared'
import * as Sentry from '@sentry/vue'
import { onMounted } from 'vue'

const emit = defineEmits<{ close: [] }>()

const trackAction = (action: 'shown' | 'dismissed' | 'reload_clicked'): void => {
  Sentry.captureMessage(`Ad blocker popup: ${action}`, {
    level: 'warning',
    tags: { feature: 'ad_blocker', action },
  })
}

onMounted(() => trackAction('shown'))

const close = (): void => {
  trackAction('dismissed')
  emit('close')
}

const reload = (): void => {
  trackAction('reload_clicked')
  window.location.reload()
}
</script>

<template>
  <DialogModal
    :responsive="false"
    class="ad-blocker-dialog"
    title="Please disable your ad blocker!"
    width="min(560px, calc(100vw - 32px))"
    align-center
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    modal-class="ad-blocker-dialog__overlay"
    @before-close="close"
  >
    <template #header>
      <FlexComponent class="ad-blocker-dialog__header" align="center" :gap="8">
        <InfoIcon aria-hidden="true" focusable="false" />
        <TypographyComponent variant="headline-medium">Please disable your ad blocker!</TypographyComponent>
      </FlexComponent>
    </template>
    <FlexComponent direction="column" :gap="16">
      <TypographyComponent variant="body-large">
        An ad blocker may be preventing Metapic from loading required data. Please allow Metapic in your ad blocker, then reload.
      </TypographyComponent>
      <TypographyComponent variant="body-large">
        Open your browser’s extensions menu, select your ad blocker, and allow Metapic on this site. If it is already allowed, check any
        other blocking extensions.
      </TypographyComponent>
    </FlexComponent>
    <template #footer>
      <ButtonComponent class="ad-blocker-dialog__reload" variant="text" @click="reload"> I’ve disabled ad blocker </ButtonComponent>
    </template>
  </DialogModal>
</template>

<style lang="scss">
.ad-blocker-dialog.el-dialog {
  background: var(--inverted);
  box-shadow: 4px 4px 32px color-mix(in srgb, var(--primary) 8%, transparent);

  .el-dialog__header {
    padding: 0 32px 24px 0;
    margin: 0;
  }

  .el-dialog__footer {
    display: flex;
    justify-content: flex-end;
    padding: 32px 0 0;
  }

  p,
  h2 {
    margin: 0;
  }

  .ad-blocker-dialog__header svg {
    flex-shrink: 0;
  }

  .ad-blocker-dialog__reload {
    padding: 8px 12px;
    font-family: inherit;
    font-weight: 600;
  }

  .el-dialog__headerbtn {
    border-radius: 40px;
  }

  .el-dialog__headerbtn:focus-visible,
  .ad-blocker-dialog__reload:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }
}
</style>
